import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { Plus, Copy, Trash2, Edit, Loader2, Eye, EyeOff, GripVertical } from "lucide-react";
import api, { resolveImg } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

export default function AdminCourses() {
  const qc = useQueryClient();
  const nav = useNavigate();
  const { data: courses, isLoading } = useQuery({ queryKey: ["admin-courses"], queryFn: async () => (await api.get("/admin/courses")).data });

  const create = useMutation({
    mutationFn: (type) => api.post("/admin/courses", { title: type === "video" ? "New Video Product" : "New Course", type }),
    onSuccess: ({ data }) => { qc.invalidateQueries({ queryKey: ["admin-courses"] }); nav(`/admin/courses/${data.id}`); },
  });
  const duplicate = useMutation({
    mutationFn: (id) => api.post(`/admin/courses/${id}/duplicate`),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["admin-courses"] }); toast.success("Course duplicated"); },
  });
  const del = useMutation({
    mutationFn: (id) => api.delete(`/admin/courses/${id}`),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["admin-courses"] }); toast.success("Course deleted"); },
  });
  const togglePublish = useMutation({
    mutationFn: ({ id, is_published }) => api.patch(`/admin/courses/${id}`, { is_published }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["admin-courses"] }),
  });

  if (isLoading) return <Loader2 className="h-8 w-8 animate-spin text-[#DCA846]" />;

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div><h1 className="font-display text-3xl font-bold uppercase tracking-tight">Courses</h1>
          <p className="mt-1 text-zinc-400">Create and manage courses & individual videos.</p></div>
        <div className="flex gap-2">
          <Button data-testid="new-video-btn" variant="outline" onClick={() => create.mutate("video")} className="border-white/15 text-white">
            <Plus className="mr-1 h-4 w-4" /> New Video
          </Button>
          <Button data-testid="new-course-btn" onClick={() => create.mutate("course")} className="bg-[#DCA846] text-black hover:bg-[#C5963B]">
            <Plus className="mr-1 h-4 w-4" /> New Course
          </Button>
        </div>
      </div>

      <div className="mt-8 space-y-3">
        {courses.length === 0 && <p className="text-zinc-500">No courses yet. Create your first one.</p>}
        {courses.map((c) => (
          <div key={c.id} data-testid={`admin-course-${c.id}`} className="flex items-center gap-4 rounded-lg border border-white/10 bg-zinc-900/50 p-4">
            <GripVertical className="hidden h-5 w-5 text-zinc-600 sm:block" />
            <img src={resolveImg(c.thumbnail || c.cover_image) || "https://images.pexels.com/photos/7697208/pexels-photo-7697208.jpeg"} alt="" className="h-14 w-24 shrink-0 rounded object-cover" />
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2">
                <p className="truncate font-display text-lg">{c.title}</p>
                {c.type === "video" && <span className="rounded bg-zinc-800 px-1.5 py-0.5 text-[10px] uppercase text-zinc-300">Video</span>}
              </div>
              <p className="text-sm text-zinc-500">{c.is_free ? "Free" : `$${Number(c.price).toFixed(2)}`} · {c.lesson_count} lessons · {c.is_published ? "Published" : "Draft"}</p>
            </div>
            <div className="flex items-center gap-1">
              <button title="Publish/Unpublish" data-testid={`toggle-publish-${c.id}`} onClick={() => togglePublish.mutate({ id: c.id, is_published: !c.is_published })}
                className="rounded p-2 text-zinc-400 hover:text-[#DCA846]">
                {c.is_published ? <Eye className="h-4 w-4" /> : <EyeOff className="h-4 w-4" />}
              </button>
              <button title="Edit" data-testid={`edit-course-${c.id}`} onClick={() => nav(`/admin/courses/${c.id}`)} className="rounded p-2 text-zinc-400 hover:text-white"><Edit className="h-4 w-4" /></button>
              <button title="Duplicate" onClick={() => duplicate.mutate(c.id)} className="rounded p-2 text-zinc-400 hover:text-white"><Copy className="h-4 w-4" /></button>
              <button title="Delete" data-testid={`delete-course-${c.id}`} onClick={() => { if (confirm("Delete this course and all its lessons?")) del.mutate(c.id); }} className="rounded p-2 text-zinc-400 hover:text-red-400"><Trash2 className="h-4 w-4" /></button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
